import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';
import { AppRole, AccessLevel, ModuleId, isAdminRole, canManageUsers, canManagePermissions } from '@/lib/rolePermissions';

interface UserPermissions {
  [module: string]: AccessLevel;
}

interface UserRoleData {
  role: AppRole | null;
  loading: boolean;
  permissions: UserPermissions;
  hasRole: (role: AppRole) => boolean;
  hasAnyRole: () => boolean;
  isAdmin: boolean;
  isCeo: boolean;
  canManageUsers: boolean;
  canManagePermissions: boolean;
  getAccessLevel: (module: ModuleId) => AccessLevel;
  canAccess: (module: ModuleId) => boolean;
  canEdit: (module: ModuleId) => boolean;
  hasFullAccess: (module: ModuleId) => boolean;
  refetch: () => Promise<void>;
}

export type { AppRole } from '@/lib/rolePermissions';

export const useUserRole = (): UserRoleData => {
  const { user, loading: authLoading } = useAuthContext();

  const { data, isLoading, refetch: queryRefetch } = useQuery({
    queryKey: ['user-role', user?.id],
    queryFn: async () => {
      const [roleResult, permResult] = await Promise.all([
        supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user!.id)
          .maybeSingle(),
        supabase
          .rpc('get_user_permissions', { p_user_id: user!.id }),
      ]);

      const role = (roleResult.data?.role as AppRole) || null;
      const permsMap: UserPermissions = {};
      if (!permResult.error && permResult.data) {
        permResult.data.forEach((p: { module: string; access: AccessLevel }) => {
          permsMap[p.module] = p.access;
        });
      }

      return { role, permissions: permsMap };
    },
    enabled: !!user && !authLoading,
  });

  const role = data?.role ?? null;
  const permissions = data?.permissions ?? {};

  const hasRole = useCallback((checkRole: AppRole): boolean => {
    return role === checkRole;
  }, [role]);

  const hasAnyRole = useCallback((): boolean => {
    return role !== null;
  }, [role]);

  const getAccessLevel = useCallback((module: ModuleId): AccessLevel => {
    return permissions[module] || 'none';
  }, [permissions]);

  const canAccess = useCallback((module: ModuleId): boolean => {
    const level = getAccessLevel(module);
    return level === 'full' || level === 'limited';
  }, [getAccessLevel]);

  const canEdit = useCallback((module: ModuleId): boolean => {
    return getAccessLevel(module) === 'full';
  }, [getAccessLevel]);

  const hasFullAccess = useCallback((module: ModuleId): boolean => {
    return getAccessLevel(module) === 'full';
  }, [getAccessLevel]);

  const isAdmin = role ? isAdminRole(role) : false;
  const isCeo = role === 'ceo';

  const refetch = async () => {
    await queryRefetch();
  };

  return {
    role,
    loading: isLoading || authLoading,
    permissions,
    hasRole,
    hasAnyRole,
    isAdmin,
    isCeo,
    canManageUsers: role ? canManageUsers(role) : false,
    canManagePermissions: role ? canManagePermissions(role) : false,
    getAccessLevel,
    canAccess,
    canEdit,
    hasFullAccess,
    refetch,
  };
};
